'use client';

import { useMemo } from 'react';

import { useTranslations } from 'next-intl';

import ProfilePredictionDialog from '@/app/[locale]/(main)/profile/_components/ProfilePredictionDialog';
import { OverlayLoading } from '@/components/shared/OverlayLoading';
import { H3 } from '@/components/shared/Typography';
import { useClaimRefunds } from '@/hooks/useClaimRefunds';
import { useClaimWinnings } from '@/hooks/useClaimWinnings';
import { usePoolsData } from '@/hooks/usePoolsData';
import { usePredictionsData } from '@/hooks/usePredictionsData';
import { useSectionState } from '@/hooks/useSectionState';
import { useTokenPools } from '@/hooks/useTokenPools';
import { useRouter } from '@/i18n/navigation';
import { cn } from '@/lib/utils/cn';
import { profileInfo } from '@/ts/constants/C_Profile';
import { T_Pool } from '@/ts/types/T_Pool';
import { T_UserBlockchainData } from '@/ts/types/T_UserBlockchainData';

import { SectionHeader } from './components/SectionHeader';
import { SectionIcons } from './components/SectionIcons';
import { SectionRightBlock } from './components/SectionRightBlock';

type T_Variant = 'predictions' | 'pools' | 'referrals';

function SectionWrapper({
  variant,
  symbol = '',
  pools,
  blockchainData
}: {
  variant: T_Variant;
  symbol?: string;
  pools?: T_Pool[];
  blockchainData?: T_UserBlockchainData;
}) {
  const t = useTranslations();
  const router = useRouter();
  // Only use profileInfo for referrals; predictions and pools don't use profileInfo
  const sectionInfo = variant === 'referrals' ? profileInfo[variant] : null;
  const titleKey = useMemo(() => {
    if (variant === 'predictions') {
      return 'profile.sections.predictions.title';
    }
    if (variant === 'pools') {
      return 'profile.sections.pools.title';
    }
    return sectionInfo?.titleKey || '';
  }, [variant, sectionInfo]);
  const emptyState = useMemo(() => {
    if (variant === 'predictions') {
      return {
        titleKey: 'profile.sections.predictions.emptyState.title',
        textKey: 'profile.sections.predictions.emptyState.text',
        actionTextKey: 'profile.sections.predictions.emptyState.actionText'
      };
    }
    if (variant === 'pools') {
      return {
        titleKey: 'profile.sections.pools.emptyState.title',
        textKey: 'profile.sections.pools.emptyState.text',
        actionTextKey: 'profile.sections.pools.emptyState.actionText'
      };
    }
    return sectionInfo?.emptyState ?? null;
  }, [variant, sectionInfo]);

  // Check isEmptyState using real data
  const isEmptyState = useMemo(() => {
    if (variant === 'predictions') {
      return !blockchainData?.predictionEventParticipations?.length;
    }
    if (variant === 'pools') {
      // Check if user has any pool balances or transactions
      const hasPoolBalances =
        blockchainData?.poolBalances && blockchainData.poolBalances.length > 0;
      const hasPoolTransactions =
        blockchainData?.poolTransactions &&
        blockchainData.poolTransactions.length > 0;
      return !hasPoolBalances && !hasPoolTransactions;
    }
    if (variant === 'referrals') {
      // Referrals data should come from real API
      // For now, always return true (empty) until real data is available
      return true;
    }
    return true;
  }, [variant, blockchainData]);

  // Static states configuration for predictions (UI structure only, data is dynamic)
  const predictionsStates =
    variant === 'predictions'
      ? [
          {
            titleKey: 'profile.sections.predictions.states.totalWon',
            className: 'bg-[image:var(--gradient-green)]'
          },
          {
            titleKey: 'profile.sections.predictions.states.totalLost',
            className: 'bg-[image:var(--custom-gray-gradient)]'
          },
          {
            titleKey: 'profile.sections.predictions.states.unclaimedWins',
            className: 'bg-[image:var(--gradient-yellow)]'
          }
        ]
      : null;
  const isPools = variant === 'pools';
  const isPredictions = variant === 'predictions';

  const {
    isClaiming,
    setIsClaiming,
    isModalClaiming,
    setIsModalClaiming,
    claimingParticipationId,
    setClaimingParticipationId,
    isModalOpen,
    setIsModalOpen,
    defaultTab,
    setDefaultTab,
    modalType,
    setModalType,
    modalVariant,
    setModalVariant,
    predictionAddress,
    setPredictionAddress,
    predictionId,
    setPredictionId,
    selectedParticipation,
    setSelectedParticipation
  } = useSectionState();

  // Claim winnings hook
  const { handleClaimWin } = useClaimWinnings({
    isClaiming,
    setIsClaiming,
    setClaimingParticipationId
  });

  // Claim refunds hook
  const { handleClaimRefund } = useClaimRefunds({
    isClaiming,
    setIsClaiming,
    setClaimingParticipationId
  });

  // Predictions data hook
  const { predictionsInfoList, predictionsHistoryList } = usePredictionsData({
    blockchainData,
    isEmptyState,
    predictionsStates,
    handleClaimWin,
    handleClaimRefund,
    claimingParticipationId,
    setDefaultTab,
    setModalType,
    setModalVariant,
    setPredictionAddress,
    setPredictionId,
    setSelectedParticipation,
    setIsModalOpen
  });

  // Pools data hook
  const { poolsHistoryList } = usePoolsData({
    blockchainData,
    isEmptyState
  });

  // Use poolsHistoryList directly - no need to wrap it
  const poolsList = poolsHistoryList;

  // Referrals functionality not implemented yet
  const referralsInfoList = null;
  const referralsList = null;

  // Token pools hook
  const { tokenPools } = useTokenPools({
    variant,
    pools,
    blockchainData
  });
  const isAnyClaiming = isClaiming || isModalClaiming;
  const claimingText = isAnyClaiming
    ? t('profile.predictionWinners.claiming') || 'Claiming...'
    : '';

  // Get first pool's tokenSymbol for pools "View all" link
  const firstPoolSymbol = useMemo(() => {
    if (variant !== 'pools') return symbol;

    // Try to get from blockchainData poolBalances first
    if (
      blockchainData?.poolBalances &&
      blockchainData.poolBalances.length > 0
    ) {
      const firstPool = blockchainData.poolBalances[0];
      return firstPool.pool.tokenSymbol?.toLowerCase() || symbol;
    }

    // Fallback to pools prop
    if (pools && pools.length > 0) {
      return pools[0].tokenSymbol?.toLowerCase() || symbol;
    }

    return symbol;
  }, [variant, blockchainData?.poolBalances, pools, symbol]);

  return (
    <div className="pb-0 py-12 md:py-11 px-4 md:px-0">
      <OverlayLoading isVisible={isAnyClaiming} text={claimingText} />
      <H3 className="mb-5">{t(titleKey)}</H3>
      <div
        className={cn(
          'mb-7 md:mb-11',
          isPools && tokenPools.length > 0 ? 'min-h-70' : 'min-h-auto'
        )}
      >
        <SectionHeader
          variant={variant}
          predictionsInfoList={predictionsInfoList}
          referralsInfoList={referralsInfoList}
          tokenPools={tokenPools}
        />
      </div>
      <div className="flex items-center gap-6">
        <div className="hidden md:block w-[50%] relative">
          <SectionIcons variant={variant} />
        </div>
        <SectionRightBlock
          variant={variant}
          symbol={firstPoolSymbol}
          isEmptyState={isEmptyState}
          emptyState={emptyState}
          predictionsHistoryList={predictionsHistoryList}
          poolsList={poolsList}
          referralsList={referralsList}
        />
      </div>
      {isPredictions && (
        <ProfilePredictionDialog
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          defaultTab={defaultTab}
          type={modalType}
          variant={modalVariant}
          predictionAddress={predictionAddress}
          predictionId={predictionId}
          selectedParticipation={selectedParticipation}
          onClaimingStateChange={setIsModalClaiming}
          onClaimSuccess={() => {
            // Refresh the page to update the prediction history with claimed status
            router.refresh();
          }}
        />
      )}
    </div>
  );
}

export default SectionWrapper;
